import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, ArrowLeft, Loader2, Search } from 'lucide-react';
import { useRewardLog } from '../hooks';
import { RewardLogTable } from '../components/RewardLogTable';

export function AdminRewardLogPage() {
  const [filterUserId, setFilterUserId] = useState('');
  const [filterGameConfigId, setFilterGameConfigId] = useState('');
  const [userInput, setUserInput] = useState('');
  const [gameInput, setGameInput] = useState('');

  const { entries, total, loading, error, loadMore, hasMore } = useRewardLog({
    userId: filterUserId || undefined,
    gameConfigId: filterGameConfigId || undefined,
  });

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    setFilterUserId(userInput.trim());
    setFilterGameConfigId(gameInput.trim());
  };

  const handleClearFilters = () => {
    setUserInput('');
    setGameInput('');
    setFilterUserId('');
    setFilterGameConfigId('');
  };

  const hasActiveFilters = filterUserId || filterGameConfigId;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_game_rewards/admin"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Admin
        </Link>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Reward Audit Log</h1>
          {total > 0 && (
            <span className="text-sm text-muted-foreground">{total} total entries</span>
          )}
        </div>
      </div>

      <form onSubmit={handleFilter} className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter by user ID..."
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            className="pl-9"
            aria-label="Filter by user ID"
          />
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter by game config ID..."
            value={gameInput}
            onChange={(e) => setGameInput(e.target.value)}
            className="pl-9"
            aria-label="Filter by game config ID"
          />
        </div>
        <Button type="submit" variant="secondary">
          Filter
        </Button>
        {hasActiveFilters && (
          <Button type="button" variant="ghost" onClick={handleClearFilters}>
            Clear
          </Button>
        )}
      </form>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {loading && entries.length === 0 ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <p className="text-center text-muted-foreground py-12">
          {hasActiveFilters
            ? 'No log entries match the current filters.'
            : 'No reward log entries yet.'}
        </p>
      ) : (
        <>
          <RewardLogTable entries={entries} showUserId />

          {hasMore && (
            <div className="text-center">
              <Button variant="outline" onClick={loadMore} disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Load more
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
